import { useCallback, useEffect, useState } from 'react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import type { Activity } from '../../api/types';
import { useProjectRole } from '../../hooks/useProjectRole';

export type CreationEntryState =
  | 'allowed'
  | 'absent'
  | 'pending'
  | 'unavailable';

/**
 * Um estado só governa todas as entradas de criação da página. `pending` e
 * `unavailable` são indeterminação, e indeterminação não libera criação
 * (fail-closed, ADR-002); `absent` é ausência de permissão, que oculta a
 * entrada sem erro.
 */
export function resolveCreationEntryState(input: {
  rbacError: boolean;
  rbacLoading: boolean;
  canCreateActivity: boolean;
}): CreationEntryState {
  if (input.rbacError) return 'unavailable';
  if (input.rbacLoading) return 'pending';
  return input.canCreateActivity ? 'allowed' : 'absent';
}

function useCurrentUserRef() {
  const identityApi = useApi(identityApiRef);
  const [currentUserRef, setCurrentUserRef] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (!cancelled) setCurrentUserRef(identity.userEntityRef);
      })
      .catch(() => {
        if (!cancelled) setCurrentUserRef(null);
      });
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  return currentUserRef;
}

export function usePortfolioAccess(openCreation: () => void) {
  const currentUserRef = useCurrentUserRef();
  const { roles, loading, error, hasPermission, isAdmin, refresh } =
    useProjectRole();
  // Both entry points stay closed while permissions are unresolved.
  const resolved = !loading && !error;
  const canCreate =
    resolved && roles.some(p => hasPermission(p.projectKey, 'activity.create'));
  const creationState = resolveCreationEntryState({
    rbacError: Boolean(error),
    rbacLoading: loading,
    canCreateActivity: canCreate,
  });
  const handleCreate = () => {
    if (canCreate) openCreation();
  };
  const isActivityLockedForUser = useCallback(
    (activity: Activity) => {
      if (currentUserRef === null) return false;
      if (activity.userId === currentUserRef) return false;
      if (activity.status === 'completed') return false;
      if (hasPermission(activity.azureProject, 'activity.bypass_owner_lock')) {
        return false;
      }
      return true;
    },
    [currentUserRef, hasPermission],
  );

  // Only the global wildcard admin can select destructive actions.
  return {
    isGlobalAdmin: resolved && isAdmin,
    creationState,
    handleCreate,
    refreshRoles: refresh,
    isActivityLockedForUser,
  };
}
export type PortfolioAccess = ReturnType<typeof usePortfolioAccess>;
